// pages/About.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Target,
  Users,
  Award,
  Globe,
  Clock,
  Shield,
  Heart,
  ChevronRight,
  ArrowRight,
  Play,
  Quote,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Star,
  TrendingUp,
  Briefcase,
  Lightbulb,
  Handshake
} from 'lucide-react';

const About = () => {
  const [activeValue, setActiveValue] = useState('innovation');

  const teamMembers = [
    {
      id: 1,
      name: 'Raza Ahmed',
      role: 'Founder & CEO',
      bio: 'With over 12 years of experience in technology, Raza leads the vision and strategy of the company.',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'raza@razatechsolutions.com'
      },
      skills: ['Leadership', 'Strategy', 'Technology']
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      role: 'CTO',
      bio: 'Expert in software architecture with 10+ years experience building scalable applications.',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
      social: {
        linkedin: 'https://linkedin.com',
        github: 'https://github.com',
        email: 'sarah@razatechsolutions.com'
      },
      skills: ['Architecture', 'DevOps', 'Security']
    },
    {
      id: 3,
      name: 'Michael Chen',
      role: 'Head of Design',
      bio: 'Creative director with 8 years of experience in UI/UX design and brand development.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
      social: {
        linkedin: 'https://linkedin.com',
        dribbble: 'https://dribbble.com',
        email: 'michael@razatechsolutions.com'
      },
      skills: ['UI/UX', 'Branding', 'Animation']
    },
    {
      id: 4,
      name: 'Emma Rodriguez',
      role: 'Marketing Director',
      bio: 'Digital marketing expert specializing in growth strategies and brand development.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
        email: 'emma@razatechsolutions.com'
      },
      skills: ['Marketing', 'SEO', 'Analytics']
    },
    {
      id: 5,
      name: 'David Kim',
      role: 'Lead Developer',
      bio: 'Full-stack developer with expertise in modern JavaScript frameworks and cloud technologies.',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
      social: {
        linkedin: 'https://linkedin.com',
        github: 'https://github.com',
        email: 'david@razatechsolutions.com'
      },
      skills: ['React', 'Node.js', 'AWS']
    },
    {
      id: 6,
      name: 'Priya Sharma',
      role: 'Project Manager',
      bio: 'PMP certified professional with 7 years of experience managing complex technology projects.',
      image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
      social: {
        linkedin: 'https://linkedin.com',
        email: 'priya@razatechsolutions.com'
      },
      skills: ['Agile', 'Scrum', 'Client Relations']
    }
  ];

  const values = [
    {
      id: 'innovation',
      title: 'Innovation',
      description: 'We constantly explore new technologies and approaches to deliver cutting-edge solutions that give our clients a competitive edge.',
      icon: <Lightbulb />
    },
    {
      id: 'excellence',
      title: 'Excellence',
      description: 'Every project receives our full attention to detail, ensuring the highest quality standards and exceptional results that exceed expectations.',
      icon: <Award />
    },
    {
      id: 'collaboration',
      title: 'Collaboration',
      description: 'We believe in working closely with our clients as partners, ensuring transparent communication and alignment throughout every project.',
      icon: <Handshake />
    },
    {
      id: 'integrity',
      title: 'Integrity',
      description: 'We maintain honesty and transparency in all our dealings, building lasting relationships based on trust and reliability.',
      icon: <Shield />
    }
  ];

  const milestones = [
    { year: '2015', event: 'Company Founded', description: 'Started with a small team of 3 passionate developers' },
    { year: '2016', event: 'First Major Project', description: 'Launched successful e-commerce platform for retail client' },
    { year: '2018', event: 'Team Expansion', description: 'Grew to 15 team members and expanded service offerings' },
    { year: '2020', event: 'Global Recognition', description: 'Awarded "Best Tech Solutions Provider" by TechAwards' },
    { year: '2022', event: 'International Clients', description: 'Started serving clients across 5 different countries' },
    { year: '2024', event: 'Current Status', description: '50+ team members, 150+ projects completed globally' }
  ];

  const achievements = [
    { number: '150+', label: 'Projects Completed', icon: <Briefcase /> },
    { number: '50+', label: 'Happy Clients', icon: <Users /> },
    { number: '98%', label: 'Client Satisfaction', icon: <Heart /> },
    { number: '12+', label: 'Industry Awards', icon: <Award /> },
    { number: '5+', label: 'Years Experience', icon: <Calendar /> },
    { number: '24/7', label: 'Support Available', icon: <Clock /> }
  ];

  const testimonials = [
    {
      name: 'TechCorp Global',
      role: 'CEO',
      content: 'Raza Tech Solutions transformed our digital infrastructure. Their expertise and dedication are unmatched.',
      rating: 5
    },
    {
      name: 'Innovate Solutions',
      role: 'CTO',
      content: 'Working with Raza Tech has been game-changing. They delivered beyond our expectations.',
      rating: 5
    },
    {
      name: 'GrowthStart Inc.',
      role: 'Marketing Director',
      content: 'Professional, timely, and exceptional quality. Truly partners in our success.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Building The Future Of <span className="text-blue-300">Technology</span>
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                At Raza Tech Solutions, we transform businesses through innovative technology 
                solutions that drive growth and create lasting impact.
              </p>
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center"
                >
                  Meet Our Team
                  <ArrowRight className="ml-2" size={20} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition flex items-center"
                >
                  <Play className="mr-2" size={20} />
                  Watch Our Story
                </motion.button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center">
                  <div className="text-6xl font-bold mb-2">2015</div>
                  <div className="text-blue-200">Year Founded</div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-8">
                  {achievements.slice(0, 4).map((achievement, index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl font-bold mb-1">{achievement.number}</div>
                      <div className="text-sm text-blue-200">{achievement.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">Journey</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              From a small startup to a trusted technology partner, our journey has been 
              fueled by passion, innovation, and a commitment to excellence.
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-100 hidden md:block"></div>
            
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative mb-12 md:mb-16 ${
                  index % 2 === 0 ? 'md:pr-1/2 md:pl-8' : 'md:pl-1/2 md:pr-8'
                }`}
              >
                <div className={`bg-white p-6 rounded-xl shadow-lg border border-gray-100 ${
                  index % 2 === 0 ? 'md:text-right' : ''
                }`}>
                  <div className="text-sm text-blue-600 font-semibold mb-2">{milestone.year}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{milestone.event}</h3>
                  <p className="text-gray-600">{milestone.description}</p>
                </div>
                {/* Timeline dot */}
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg hidden md:block"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">Core Values</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do, from client interactions to technology choices.
            </p>
          </motion.div>

          {/* Value Tabs */}
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              {values.map((value) => (
                <motion.button
                  key={value.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveValue(value.id)}
                  className={`flex flex-col items-center p-4 rounded-xl transition-all ${
                    activeValue === value.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                    activeValue === value.id
                      ? 'bg-white/20'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    {value.icon}
                  </div>
                  <span className="font-semibold">{value.title}</span>
                </motion.button>
              ))}
            </div>

            {/* Value Description */}
            <motion.div
              key={activeValue}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-start mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mr-6">
                  {values.find(v => v.id === activeValue)?.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {values.find(v => v.id === activeValue)?.title}
                  </h3>
                  <p className="text-gray-600 text-lg">
                    {values.find(v => v.id === activeValue)?.description}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Meet Our Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our <span className="text-blue-600">Leadership Team</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our talented team of experts brings diverse skills and experiences to deliver 
              exceptional results for our clients.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                {/* Team Member Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-2xl font-bold text-white">{member.name}</h3>
                    <p className="text-blue-200">{member.role}</p>
                  </div>
                </div>

                {/* Team Member Info */}
                <div className="p-6">
                  <p className="text-gray-600 mb-4">{member.bio}</p>
                  
                  {/* Skills */}
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {member.skills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="flex space-x-3">
                    {member.social.linkedin && (
                      <a
                        href={member.social.linkedin}
                        className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-blue-100 hover:text-blue-600 transition"
                      >
                        <Linkedin size={20} />
                      </a>
                    )}
                    {member.social.twitter && (
                      <a
                        href={member.social.twitter}
                        className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-blue-100 hover:text-blue-600 transition"
                      >
                        <Twitter size={20} />
                      </a>
                    )}
                    {member.social.github && (
                      <a
                        href={member.social.github}
                        className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                      </a>
                    )}
                    {member.social.email && (
                      <a
                        href={`mailto:${member.social.email}`}
                        className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-red-100 hover:text-red-600 transition"
                      >
                        <Mail size={20} />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Join Team CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Want to Join Our Team?
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                We're always looking for talented individuals who share our passion 
                for technology and innovation.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                View Open Positions
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-blue-200">Achievements</span>
            </h2>
            <p className="text-blue-100 max-w-3xl mx-auto">
              Milestones that reflect our commitment to excellence and innovation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  {achievement.icon}
                </div>
                <div className="text-4xl font-bold mb-2">{achievement.number}</div>
                <div className="text-blue-100">{achievement.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-blue-600">Clients Say</span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our clients have to say 
              about working with us.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-8 relative"
              >
                <Quote className="absolute top-4 right-4 w-8 h-8 text-blue-200" />
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic text-lg">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-blue-600 font-bold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Let's Build Something Amazing Together
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Ready to transform your business with innovative technology solutions? 
              Get in touch with our team today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Contact Us Now
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition"
              >
                Schedule a Call
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;